import React, { useState, useEffect } from "react";
import serviceIcon01 from "../../images/icon/service-icon01.svg";
import serviceIcon02 from "../../images/icon/service-icon02.svg";
import serviceIcon03 from "../../images/icon/service-icon03.svg";
import serviceIcon04 from "../../images/icon/service-icon04.svg";
import serviceIcon05 from "../../images/icon/service-icon05.svg";
import serviceIcon06 from "../../images/icon/service-icon06.svg";
import serviceIcon07 from "../../images/icon/service-icon07.svg";
import feaIcon01 from "../../images/icon/fea-small-icon01.svg";
import feaIcon02 from "../../images/icon/fea-small-icon02.svg";
import feaIcon03 from "../../images/icon/fea-small-icon03.svg";
import diamondIcon from "../../images/icon/diamond-icon01.gif";
import serviceBg from "../../images/bg/service-gradient-bg.png";

const iconMap = [
  serviceIcon01, serviceIcon02, serviceIcon03, serviceIcon04, serviceIcon05,
  serviceIcon06, serviceIcon07, feaIcon01, feaIcon02, feaIcon03,
];
const colorMap = [
  "#00ff97", "#00D9F5", "#A855F7",
  "#00ff97", "#00D9F5", "#A855F7",
  "#00ff97", "#00D9F5", "#A855F7", "#00ff97",
];

const services = [
  {
    num: "01",
    short: "WhatsApp AI Layer",
    title: "WhatsApp AI Automation Layer",
    tagline: "Turn WhatsApp into a fully automated business channel",
    desc: "We build a complete AI-powered layer on top of WhatsApp Business. Your business handles hundreds of conversations simultaneously — answering questions, capturing leads, booking appointments — without any human involvement.",
    bullets: [
      "Auto-reply to FAQs instantly",
      "Lead capture → auto-push to CRM",
      "Appointment booking inside WhatsApp",
      "Order confirmations & delivery updates",
      "Multilingual support across languages",
    ],
    stack: "WhatsApp Cloud API · n8n · Claude AI · Supabase",
    bestFor: "Retail, real estate, clinics, hospitality",
    stat: { value: "24/7", label: "Automated" },
  },
  {
    num: "02",
    short: "RAG Knowledge System",
    title: "RAG-Based Knowledge System",
    tagline: "AI that actually knows your business",
    desc: "Train your AI on YOUR specific data — product catalogues, SOPs, contracts, policies. Customers and staff get accurate answers pulled directly from your actual documents, not generic AI guesses.",
    bullets: [
      "PDF, DOCX, XLSX — all become searchable",
      "Semantic search understands meaning",
      "Answers grounded in your real data",
      "Source citation per answer",
      "Works on WhatsApp, web chat, email",
    ],
    stack: "Supabase pgvector · Claude AI · n8n",
    bestFor: "Companies with large catalogues, SOPs, compliance docs",
    stat: { value: "99%", label: "Answer Accuracy" },
  },
  {
    num: "03",
    short: "AI Personal Assistant",
    title: "AI Personal Assistant",
    tagline: "A smart executive assistant, available 24/7",
    desc: "An AI assistant configured specifically for you — handles scheduling, drafts emails, summarises documents, creates reports, and manages tasks — triggered by a simple WhatsApp message.",
    bullets: [
      "Calendar management & meeting scheduling",
      "Email drafting from keywords or voice notes",
      "Document summarisation in seconds",
      "On-demand reports from connected tools",
      "Reminders and task follow-up",
    ],
    stack: "Claude AI · n8n · Google APIs · Supabase",
    bestFor: "CEOs, managers, business owners",
    stat: { value: "5h+", label: "Saved Per Day" },
  },
  {
    num: "04",
    short: "Workflow Automation",
    title: "Workflow & Process Automation",
    tagline: "Your business processes running on autopilot",
    desc: "We rebuild your multi-step manual processes as fully automated flows. Anything that involves copy-pasting, chasing people, or repetitive clicks — replaced with reliable automated workflows.",
    bullets: [
      "Zoho, SAP, Odoo, HubSpot, Salesforce integrations",
      "Any event triggers a downstream action chain",
      "Data sync across platforms automatically",
      "Approval routing via WhatsApp or email",
      "Run any flow on a schedule",
    ],
    stack: "n8n · REST APIs · Webhooks",
    bestFor: "Operations-heavy businesses, teams with manual data work",
    stat: { value: "10x", label: "Faster Ops" },
  },
  {
    num: "05",
    short: "Lead Capture & CRM",
    title: "Lead Capture & CRM Automation",
    tagline: "Never lose a lead again",
    desc: "From the moment a prospect reaches out — WhatsApp, website, social media — they are captured, qualified by AI, assigned to the right person, and followed up with, automatically.",
    bullets: [
      "Multi-source capture (WhatsApp, forms, Instagram)",
      "AI lead scoring and qualification",
      "Auto-push to Zoho, HubSpot, or Salesforce",
      "Auto-assignment to the right salesperson",
      "Timed follow-up sequences",
    ],
    stack: "n8n · Claude AI · WhatsApp Cloud API · CRM APIs",
    bestFor: "Real estate, B2B, clinics, sales-driven businesses",
    stat: { value: "70%", label: "More Conversions" },
  },
  {
    num: "06",
    short: "Automated Reporting",
    title: "Automated Reporting & Data Pipelines",
    tagline: "The right numbers, in your hands, on schedule",
    desc: "Automated pipelines that pull from your tools, process the data, and deliver formatted reports on schedule or on demand. No more manual Excel work.",
    bullets: [
      "Data pull from ERP, CRM, spreadsheets, APIs",
      "Reports delivered to inbox or WhatsApp on schedule",
      "Ask your AI for any report instantly",
      "Threshold alerts when KPIs cross limits",
      "Excel, PDF, Google Sheets, or WhatsApp summary",
    ],
    stack: "n8n · Supabase · Google Sheets API · PDF generation",
    bestFor: "COOs, operations managers, business owners",
    stat: { value: "100%", label: "Data Accuracy" },
  },
  {
    num: "07",
    short: "AI Customer Support",
    title: "AI Customer Support Agent",
    tagline: "24/7 customer support — zero extra headcount",
    desc: "A fully autonomous AI support agent on your website or WhatsApp. Handles tickets, answers product questions, processes complaints, and escalates to a human only when genuinely needed.",
    bullets: [
      "Responds within seconds, any time of day",
      "Based on your actual catalogue and policies",
      "Live order & account lookups",
      "Full ticket history logged automatically",
      "Sentiment detection → priority human follow-up",
    ],
    stack: "Claude AI · Supabase pgvector · n8n · WhatsApp / Web",
    bestFor: "E-commerce, retail, clinics, service businesses",
    stat: { value: "80%", label: "Tickets Resolved" },
  },
  {
    num: "08",
    short: "Internal AI Assistant",
    title: "Internal AI Assistant",
    tagline: "Your company knowledge, instantly accessible to staff",
    desc: "An internal AI trained on your SOPs, HR policies, and databases. Staff ask questions, retrieve documents, and trigger workflows — without bothering managers or digging through shared drives.",
    bullets: [
      "Instant SOP & policy answers",
      "HR self-service (leave, payroll, policies)",
      "Document retrieval by description",
      "Onboarding assistant for new employees",
      "Department-specific access controls",
    ],
    stack: "Claude AI · Supabase pgvector · n8n · WhatsApp / Web",
    bestFor: "Companies with 10+ employees, distributed teams",
    stat: { value: "3h", label: "Saved Per Staff" },
  },
  {
    num: "09",
    short: "Social Media Automation",
    title: "Social Media & Review Automation",
    tagline: "Stay active on social without the daily effort",
    desc: "Automate your social presence and online reputation. From scheduling content to responding to Google reviews and DMs — your brand stays active and responsive automatically.",
    bullets: [
      "Real-time Google & review platform monitoring",
      "Auto-respond to positive reviews on-brand",
      "Instant alerts for negative reviews",
      "Auto-reply to common Instagram/Facebook DMs",
      "AI-assisted content scheduling",
    ],
    stack: "n8n · Claude AI · Meta Graph API · Google Business API",
    bestFor: "Restaurants, retail, clinics, hospitality",
    stat: { value: "4.9★", label: "Avg Review" },
  },
  {
    num: "10",
    short: "Custom AI Agents",
    title: "Custom AI Agent Development",
    tagline: "Purpose-built AI for your unique problem",
    desc: "When your process is too specific for off-the-shelf tools, we build a custom AI agent from scratch — tailored logic, tailored interface, tailored outcome.",
    bullets: [
      "Document intelligence (invoices, contracts, forms)",
      "Procurement & PO processing automation",
      "Compliance rule checking",
      "Multi-step research and reporting agents",
      "Voice note → CRM / task / email workflows",
    ],
    stack: "Claude AI (tool use) · n8n · Supabase · Custom APIs",
    bestFor: "Enterprise, highly specific processes",
    stat: { value: "100%", label: "Bespoke Build" },
  },
];

const rgbFor = (c: string) =>
  c === "#00ff97" ? "0,255,151" : c === "#00D9F5" ? "0,217,245" : "168,85,247";

// ── Chevron icon ────────────────────────────────────────────────────────────
const Chevron: React.FC<{ open: boolean }> = ({ open }) => (
  <svg
    width="16" height="16" viewBox="0 0 16 16" fill="none"
    style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.25s ease", flexShrink: 0 }}
  >
    <path d="M3 6l5 5 5-5" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ── Shared content block (used by both desktop panel & mobile accordion) ──
interface ContentProps {
  svc: typeof services[0];
  color: string;
  icon: string;
  compact?: boolean;
}
const ServiceContent: React.FC<ContentProps> = ({ svc, color, icon, compact = false }) => {
  const rgb = rgbFor(color);
  const pad = compact ? "16px" : "36px 40px";
  return (
    <div style={{ padding: compact ? 0 : pad }}>
      {/* Icon + number */}
      {!compact && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
          <div style={{
            width: 52, height: 52, borderRadius: 13,
            background: `rgba(${rgb},0.1)`, border: `1px solid rgba(${rgb},0.2)`,
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}>
            <img src={icon} alt="" aria-hidden="true" style={{ width: 26 }} />
          </div>
          <span style={{
            fontFamily: "var(--font-heading)", fontSize: "clamp(2rem,4vw,3.2rem)",
            fontWeight: 800, color: `rgba(${rgb},0.12)`, lineHeight: 1, userSelect: "none",
          }}>
            {svc.num}
          </span>
        </div>
      )}

      {/* Tagline pill */}
      <span style={{
        display: "inline-block",
        background: `rgba(${rgb},0.08)`, border: `1px solid rgba(${rgb},0.2)`,
        color, fontSize: compact ? "0.6rem" : "0.67rem", fontWeight: 600,
        letterSpacing: "1.4px", textTransform: "uppercase",
        padding: compact ? "3px 10px" : "4px 12px", borderRadius: 20,
        marginBottom: compact ? 10 : 12,
      }}>
        {svc.tagline}
      </span>

      {/* Description */}
      <p style={{
        color: "rgba(255,255,255,0.55)", lineHeight: 1.75,
        fontSize: compact ? "0.86rem" : "0.92rem", marginBottom: compact ? 14 : 20,
      }}>
        {svc.desc}
      </p>

      {/* Divider */}
      <div style={{ height: 1, background: "rgba(255,255,255,0.06)", marginBottom: compact ? 14 : 20 }} />

      {/* Bullets */}
      <ul style={{
        listStyle: "none", padding: 0, margin: `0 0 ${compact ? 14 : 20}px`,
        display: "flex", flexDirection: "column", gap: compact ? 7 : 9,
      }}>
        {svc.bullets.map((b, bi) => (
          <li key={bi} style={{
            display: "flex", alignItems: "flex-start", gap: 9,
            fontSize: compact ? "0.83rem" : "0.875rem",
            color: "rgba(255,255,255,0.65)", lineHeight: 1.5,
          }}>
            <span style={{
              width: 5, height: 5, borderRadius: "50%", background: color,
              flexShrink: 0, marginTop: 5, boxShadow: `0 0 5px ${color}80`,
            }} />
            {b}
          </li>
        ))}
      </ul>

      {/* Stat + Stack row */}
      <div style={{ display: "flex", gap: 10, marginBottom: compact ? 14 : 20 }}>
        <div style={{
          flex: 1, background: `rgba(${rgb},0.07)`, border: `1px solid rgba(${rgb},0.18)`,
          borderRadius: 10, padding: compact ? "12px 14px" : "14px 18px", textAlign: "center",
        }}>
          <div style={{
            fontFamily: "var(--font-heading)", fontSize: compact ? "1.5rem" : "1.9rem",
            fontWeight: 800, color, lineHeight: 1, marginBottom: 4,
          }}>{svc.stat.value}</div>
          <div style={{
            fontSize: "0.58rem", color: "rgba(255,255,255,0.3)",
            textTransform: "uppercase", letterSpacing: "1.2px",
          }}>{svc.stat.label}</div>
        </div>
        <div style={{
          flex: 1, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)",
          borderRadius: 10, padding: compact ? "12px 14px" : "14px 18px",
        }}>
          <div style={{
            fontSize: "0.55rem", color: "rgba(255,255,255,0.25)",
            letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 5,
          }}>Stack</div>
          <div style={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.45)", lineHeight: 1.55 }}>
            {svc.stack}
          </div>
        </div>
      </div>

      {/* Best For */}
      <div style={{
        display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8,
        padding: compact ? "8px 12px" : "10px 14px",
        background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.05)",
        borderRadius: 9, marginBottom: compact ? 16 : 24,
      }}>
        <span style={{
          fontSize: "0.58rem", color: "rgba(255,255,255,0.25)",
          letterSpacing: "1.5px", textTransform: "uppercase", flexShrink: 0,
        }}>Best For</span>
        <span style={{ fontSize: compact ? "0.78rem" : "0.82rem", color: "rgba(255,255,255,0.5)" }}>
          {svc.bestFor}
        </span>
      </div>

      {/* CTA */}
      <a href="#contact" className="thm-btn agency-btn"
        style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
        <span className="text">Book a Free Call</span>
      </a>
    </div>
  );
};

// ── Main component ───────────────────────────────────────────────────────────
const ServiceSection: React.FC = () => {
  const [active, setActive] = useState(0);
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== "undefined" ? window.innerWidth >= 992 : true
  );

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const onResize = () => {
      clearTimeout(timer);
      // Debounce 150ms so resize doesn't cause rapid re-renders / jerk
      timer = setTimeout(() => setIsDesktop(window.innerWidth >= 992), 150);
    };
    window.addEventListener("resize", onResize);
    return () => { window.removeEventListener("resize", onResize); clearTimeout(timer); };
  }, []);

  const svc   = services[active];
  const icon  = iconMap[active];
  const color = colorMap[active];
  const rgb   = rgbFor(color);

  return (
    <section
      className="pos-rel pt-145 pb-145"
      id="services"
      style={{ background: `url(${serviceBg}) no-repeat center center`, backgroundSize: "cover" }}
    >
      <div className="container">

        {/* ── Header ── */}
        <div
          className="sec-title sec-title-center fea-sec-title text-center mb-70 wow fadeInUp"
          data-wow-duration="800ms"
        >
          <span className="sub-title mb-15">Our Services</span>
          {/* Pure inline flow — image is inline so text + diamond never overlap */}
          <h2 className="title" style={{ textAlign: "center", lineHeight: 1.35 }}>
            10 ways we automate{" "}
            <img
              src={diamondIcon} alt="" aria-hidden="true"
              style={{ width: 36, height: 36, verticalAlign: "middle", display: "inline", margin: "0 4px" }}
            />
            {" "}your business
          </h2>
          <p style={{ color: "var(--color-gray)", maxWidth: 560, margin: "18px auto 0", lineHeight: 1.75, fontSize: "1rem" }}>
            Every service is standalone or combines into a unified automation ecosystem — built on Claude AI, n8n, Supabase, and WhatsApp Cloud API.
          </p>
        </div>

        {/* ══════════════════ DESKTOP LAYOUT ══════════════════ */}
        {isDesktop && (
          <div
            className="wow fadeInUp"
            data-wow-delay="150ms"
            data-wow-duration="800ms"
            style={{ display: "flex", gap: 24, alignItems: "flex-start" }}
          >
            {/* Left nav */}
            <div style={{ width: 280, flexShrink: 0 }}>
              <nav aria-label="Services" style={{
                background: "rgba(255,255,255,0.02)",
                border: "1px solid rgba(255,255,255,0.06)",
                borderRadius: 16, overflow: "hidden",
              }}>
                {services.map((s, idx) => {
                  const c = colorMap[idx];
                  const isActive = active === idx;
                  return (
                    <button
                      key={idx}
                      onClick={() => setActive(idx)}
                      aria-pressed={isActive}
                      style={{
                        width: "100%", textAlign: "left",
                        background: isActive ? `rgba(${rgbFor(c)},0.06)` : "transparent",
                        borderLeft: `2px solid ${isActive ? c : "transparent"}`,
                        borderRight: 0, borderTop: 0,
                        borderBottom: "1px solid rgba(255,255,255,0.04)",
                        padding: "12px 16px", cursor: "pointer",
                        display: "flex", alignItems: "center", gap: 10,
                        transition: "background 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        if (!isActive) (e.currentTarget as HTMLElement).style.background = "rgba(255,255,255,0.03)";
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive) (e.currentTarget as HTMLElement).style.background = "transparent";
                      }}
                    >
                      <span style={{
                        fontFamily: "'Courier New', monospace", fontSize: "0.6rem",
                        fontWeight: 700, letterSpacing: "1.5px",
                        color: isActive ? c : "rgba(255,255,255,0.2)", minWidth: 18, transition: "color 0.2s",
                      }}>{s.num}</span>
                      <span style={{
                        fontSize: "0.86rem", fontWeight: isActive ? 600 : 400,
                        color: isActive ? "#fff" : "rgba(255,255,255,0.5)",
                        flex: 1, transition: "color 0.2s",
                      }}>{s.short}</span>
                      {isActive && (
                        <span style={{
                          width: 5, height: 5, borderRadius: "50%",
                          background: c, boxShadow: `0 0 8px ${c}`, flexShrink: 0,
                        }} />
                      )}
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Right content panel */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                background: "rgba(255,255,255,0.025)",
                border: "1px solid rgba(255,255,255,0.07)",
                borderTop: `2px solid ${color}`,
                borderRadius: 16, padding: "36px 40px",
                position: "relative", overflow: "hidden",
                transition: "border-color 0.3s ease",
              }}>
                {/* Glow */}
                <div aria-hidden="true" style={{
                  position: "absolute", top: -60, right: -60,
                  width: 220, height: 220, borderRadius: "50%",
                  background: `radial-gradient(circle, rgba(${rgb},0.09) 0%, transparent 70%)`,
                  pointerEvents: "none", transition: "all 0.4s ease",
                }} />
                <ServiceContent svc={svc} color={color} icon={icon} />
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════ MOBILE LAYOUT — accordion cards ══════════════════ */}
        {!isDesktop && (
          <div
            className="wow fadeInUp"
            data-wow-delay="100ms"
            data-wow-duration="600ms"
            style={{ display: "flex", flexDirection: "column", gap: 8 }}
          >
            {services.map((s, idx) => {
              const c = colorMap[idx];
              const isActive = active === idx;
              const sRgb = rgbFor(c);
              const ico = iconMap[idx];
              return (
                <div key={idx} style={{
                  background: "rgba(255,255,255,0.02)",
                  border: `1px solid ${isActive ? c : "rgba(255,255,255,0.07)"}`,
                  borderRadius: 14, overflow: "hidden",
                  transition: "border-color 0.25s ease",
                }}>
                  {/* Accordion header */}
                  <button
                    onClick={() => setActive(idx)}
                    aria-expanded={isActive}
                    style={{
                      width: "100%",
                      background: isActive ? `rgba(${sRgb},0.07)` : "transparent",
                      border: "none",
                      borderBottom: isActive ? "1px solid rgba(255,255,255,0.06)" : "none",
                      padding: "14px 16px", cursor: "pointer",
                      display: "flex", alignItems: "center", gap: 12,
                      transition: "background 0.2s",
                    }}
                  >
                    {/* Icon box */}
                    <div style={{
                      width: 38, height: 38, borderRadius: 10,
                      background: `rgba(${sRgb},0.1)`, border: `1px solid rgba(${sRgb},0.2)`,
                      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                    }}>
                      <img src={ico} alt="" aria-hidden="true" style={{ width: 20 }} />
                    </div>
                    {/* Number + title */}
                    <div style={{ flex: 1, textAlign: "left" }}>
                      <div style={{
                        fontFamily: "'Courier New', monospace", fontSize: "0.58rem",
                        fontWeight: 700, letterSpacing: "1.5px",
                        color: isActive ? c : "rgba(255,255,255,0.3)", marginBottom: 2,
                      }}>{s.num}</div>
                      <div style={{
                        fontSize: "0.88rem", fontWeight: 600,
                        color: isActive ? "#fff" : "rgba(255,255,255,0.7)",
                      }}>{s.title}</div>
                    </div>
                    <Chevron open={isActive} />
                  </button>

                  {/* Accordion body */}
                  {isActive && (
                    <div style={{
                      padding: "18px 16px",
                      borderTop: `2px solid ${c}`,
                      background: "rgba(255,255,255,0.01)",
                    }}>
                      <ServiceContent svc={s} color={c} icon={ico} compact />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

      </div>
    </section>
  );
};

export default ServiceSection;
